/**
 * Camera occlusion avoidance.
 *
 * Two probes, both allocation-free on the hot path:
 *
 *  1. `terrainSweep()` marches the segment focus→camera against
 *     `world.terrain.heightAt` and returns the largest fraction of the boom
 *     that stays `clearance` metres above the ground. Pulling the boom IN is
 *     preferred over lifting the camera: lifting tilts the rig further down and
 *     costs sky, which is the one thing the DW reference plate is strict about.
 *
 *  2. `propSweep()` tests the same segment against `world.props.blockers` —
 *     ~490 trunk-sized cylinders `{x, z, y0, y1, r}` published for exactly this
 *     (docs/REQUESTS.md). It is a 2D ray/circle solve plus a height-span test
 *     per blocker with an AABB reject in front, so the whole set costs less
 *     than one fort raycast and can run every frame. Only the TRUNK is a
 *     blocker: a boom should slide round a tree, not be shoved out of the
 *     fight by its canopy.
 *
 *  3. `fortSweep()` raycasts the same segment against the walled sites in
 *     `world.bases.list`. A fort is one merged mesh, so this is only worth
 *     doing when the boom is actually near one — the 2D segment/circle test in
 *     `nearSite()` rejects every other frame for free, and the result is held
 *     for `PROBE_EVERY` frames so a 20 k-triangle wall is never walked twice in
 *     a row.
 *
 * All three return a fraction in (0,1]; the rig takes the smallest.
 */
import * as THREE from 'three';

const _ray = new THREE.Raycaster();
const _org = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _hits = [];

/** Sites that actually have something tall to clip through. */
const WALLED = { fort: 1, post: 1, camp: 0.55, bridge: 0 };

/**
 * Largest safe fraction of the boom against the heightfield.
 * `steps` samples plus one bisection refinement — 10 heightAt() calls.
 */
export function terrainSweep(terrain, fx, fy, fz, cx, cy, cz, clearance, steps = 7) {
  if (!terrain) return 1;
  const dx = cx - fx, dy = cy - fy, dz = cz - fz;
  let last = 0;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const px = fx + dx * t, pz = fz + dz * t, py = fy + dy * t;
    if (py < terrain.heightAt(px, pz) + clearance) {
      // bisect once between the last clear sample and this one
      let lo = last, hi = t;
      for (let k = 0; k < 3; k++) {
        const m = (lo + hi) * 0.5;
        const mx = fx + dx * m, mz = fz + dz * m, my = fy + dy * m;
        if (my < terrain.heightAt(mx, mz) + clearance) hi = m; else lo = m;
      }
      return lo;
    }
    last = t;
  }
  return 1;
}

/**
 * Largest safe fraction of the boom against `world.props.blockers`.
 *
 * For each trunk we solve the 2D ray/circle intersection along focus→camera and
 * take the ENTRY parameter, then reject the trunk if the boom passes clean over
 * or under it at that point. Returns the smallest surviving entry parameter.
 */
export function propSweep(w, fx, fy, fz, cx, cy, cz, clearance) {
  const list = w.props && w.props.blockers;
  if (!list || !list.length) return 1;
  const dx = cx - fx, dz = cz - fz, dy = cy - fy;
  const l2 = dx * dx + dz * dz;
  if (l2 < 0.04) return 1;
  const len = Math.sqrt(l2 + dy * dy);
  // segment AABB, so 490 trunks cost two compares each in the common case
  const lo_x = (dx < 0 ? cx : fx), hi_x = (dx < 0 ? fx : cx);
  const lo_z = (dz < 0 ? cz : fz), hi_z = (dz < 0 ? fz : cz);
  let best = 1;
  for (let i = 0; i < list.length; i++) {
    const b = list[i];
    const R = b.r + clearance;
    if (b.x < lo_x - R || b.x > hi_x + R || b.z < lo_z - R || b.z > hi_z + R) continue;
    const ox = fx - b.x, oz = fz - b.z;
    const bq = 2 * (ox * dx + oz * dz);
    const cq = ox * ox + oz * oz - R * R;
    const disc = bq * bq - 4 * l2 * cq;
    if (disc <= 0) continue;
    const sq = Math.sqrt(disc);
    let t0 = (-bq - sq) / (2 * l2);
    const t1 = (-bq + sq) / (2 * l2);
    if (t1 <= 0 || t0 >= 1) continue;
    if (t0 < 0) t0 = 0;
    // does the boom actually pass through the trunk's height span?
    const ya = fy + dy * t0, yb = fy + dy * (t1 > 1 ? 1 : t1);
    const yLo = ya < yb ? ya : yb, yHi = ya < yb ? yb : ya;
    if (yHi < b.y0 || yLo > b.y1) continue;
    const f = t0 - clearance / len;
    if (f < best) best = f < 0.12 ? 0.12 : f;
  }
  return best;
}

/** 2D distance from a site centre to the focus→camera segment. */
function segDist(bx, bz, fx, fz, cx, cz) {
  const dx = cx - fx, dz = cz - fz;
  const l2 = dx * dx + dz * dz;
  let t = l2 > 1e-6 ? ((bx - fx) * dx + (bz - fz) * dz) / l2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const px = fx + dx * t - bx, pz = fz + dz * t - bz;
  return Math.hypot(px, pz);
}

/**
 * Raycast the boom against nearby walled sites. `state` is a caller-owned
 * scratch record ({ frac, frame }) so the result can be held between probes.
 */
export function fortSweep(w, state, frame, fx, fy, fz, cx, cy, cz, clearance, probeEvery = 2) {
  const bases = w.bases;
  if (!bases || !bases.list || !bases.list.length) return 1;
  if (frame - state.frame < probeEvery) return state.frac;
  state.frame = frame;

  const len = Math.hypot(cx - fx, cy - fy, cz - fz);
  if (len < 0.2) { state.frac = 1; return 1; }

  let best = 1;
  const list = bases.list;
  for (let i = 0; i < list.length; i++) {
    const b = list[i];
    const solid = WALLED[b.type];
    if (!solid || !b.group) continue;
    // cheap rejects, in cost order: the hero is nowhere near this site, or
    // the boom never comes near it. A fort is ONE merged mesh, so the point of
    // these is to make sure we almost never walk its triangles.
    const bx = b.x - fx, bz = b.z - fz;
    if (bx * bx + bz * bz > (b.radius + 12) * (b.radius + 12)) continue;
    if (segDist(b.x, b.z, fx, fz, cx, cz) > b.radius + 4) continue;

    _org.set(fx, fy, fz);
    _dir.set(cx - fx, cy - fy, cz - fz).multiplyScalar(1 / len);
    _ray.set(_org, _dir);
    _ray.near = 0.35;
    _ray.far = len;
    _hits.length = 0;
    _ray.intersectObject(b.group, true, _hits);
    for (let k = 0; k < _hits.length; k++) {
      const d = _hits[k].distance;
      if (d <= 0.35) continue;
      const f = Math.max(0.12, (d - clearance) / len);
      if (f < best) best = f;
      break;                                   // hits are distance-sorted
    }
    _hits.length = 0;
  }
  state.frac = best;
  return best;
}
